import { useEffect, useState, useRef } from 'react';

interface VersionInfo {
  version: string;
  timestamp: number;
}

const VERSION_CHECK_INTERVAL = 300000; // Check every 5 minutes (reduced from 60 seconds for efficiency)
const VERSION_FILE = '/version.json';
const VERSION_STORAGE_KEY = 'app_version';

export const useVersionCheck = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const currentVersionRef = useRef<string | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const checkVersion = async () => {
    try {
      // Fetch version file with cache-busting query parameter
      const response = await fetch(`${VERSION_FILE}?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        console.warn('Version check: Failed to fetch version.json', response.status);
        return;
      }

      const versionInfo: VersionInfo = await response.json();
      const newVersion = `${versionInfo.version}-${versionInfo.timestamp}`;

      // If no stored version, this is the first check - store it
      if (currentVersionRef.current === null) {
        currentVersionRef.current = newVersion;
        localStorage.setItem(VERSION_STORAGE_KEY, newVersion);
        console.log('Version check: Initial version set to', newVersion);
        return;
      }

      // Check if version has changed
      if (currentVersionRef.current !== newVersion) {
        console.log('Version check: Update detected!', {
          current: currentVersionRef.current,
          new: newVersion
        });
        setShowUpdate(true);
        // Clear the interval once update is detected
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
      } else {
        console.log('Version check: No update (current:', currentVersionRef.current, ')');
      }
    } catch (error) {
      // Log errors for debugging
      console.error('Version check failed:', error);
    }
  };

  useEffect(() => {
    // Clean up cache-busting parameter from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('v')) {
      const cleanUrl = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, '', cleanUrl);
    }

    // Load stored version from localStorage first
    const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
    if (storedVersion) {
      currentVersionRef.current = storedVersion;
      console.log('Version check: Loaded stored version', storedVersion);
    }

    // Initial check immediately, then set up periodic checking
    checkVersion();

    // Set up periodic checking
    checkIntervalRef.current = setInterval(() => {
      checkVersion();
    }, VERSION_CHECK_INTERVAL);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  const handleRefresh = () => {
    // Update stored version before refreshing
    const versionInfo = currentVersionRef.current;
    if (versionInfo) {
      // Get the latest version from the server
      fetch(`${VERSION_FILE}?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      })
        .then(res => res.json())
        .then(data => {
          const latestVersion = `${data.version}-${data.timestamp}`;
          localStorage.setItem(VERSION_STORAGE_KEY, latestVersion);
        })
        .catch(() => {
          // If fetch fails, just proceed with refresh
        });
    }
    
    // Force a hard refresh that bypasses cache
    // Using replace() with cache-busting parameter ensures cache is bypassed
    const currentUrl = window.location.href.split('?')[0];
    const cacheBuster = `?v=${Date.now()}`;
    window.location.replace(currentUrl + cacheBuster);
  };

  const handleDismiss = () => {
    // Update the stored version to the new one, but don't refresh
    // This allows users to dismiss and continue working, but they'll be prompted again
    // if they stay on the page and another update comes
    setShowUpdate(false);
    // Optionally, you could update currentVersionRef here if you want to suppress
    // the notification until the next version change
  };

  return {
    showUpdate,
    handleRefresh,
    handleDismiss
  };
};

